<template>
  <section class="main" sticky-container>
    <loader class="main__loader" v-if="!isLoaded"></loader>
    <template v-else>
      <div class="main__content main__content_a">
        <back-link
          v-if="backLink"
          :to="backLink.to"
          :programmatic="backLink.isProgrammatic"
          class="back-link"
        >
          {{ backLink.label }}
        </back-link>

        <div class="banners" v-if="installationFailureReason || downloadFailureReason">
          <div v-if="installationFailureReason" class="banner">
            <html-with-links tag="p" :html="installationFailureReason" />
          </div>

          <div v-if="downloadFailureReason" class="banner">
            <html-with-links tag="p" :html="downloadFailureReason" />
          </div>
        </div>

        <div class="section heading">
          <img
            v-if="hasIcon"
            class="heading__icon"
            :src="ug.object.icon_uri"
            @error="iconFailedToLoad = true"
          />
          <div v-else class="heading__icon-stub"></div>
          <div
            ref="desc"
            class="heading__desc"
            :class="{
              heading__desc_expanded: isDescExpanded || editMode,
            }"
          >
            <div class="heading__mobile">
              <img
                v-if="hasIcon"
                class="heading__icon-mobile"
                :src="ug.object.icon_uri"
              />
              <div v-else class="heading__icon-stub-mobile"></div>
              <div>
                <h1 class="heading__title">
                  <editable-line v-model="ug.object.name" :placeholder="$t('APP_NAME')"
                                 field="name"/>
                  <span class="heading__badge" v-if="ug.object.type === 'tweak'">
                    {{ $t('TWEAKED') }}
                  </span>
                </h1>
                <p
                  v-if="!editMode && ug.object.lead"
                  class="short-description"
                >
                  {{ ug.object.lead }}
                </p>
              </div>
            </div>

            <template v-if="editMode">
              <h3 class="h3">{{ $t('SHORT_DESCRIPTION') }}</h3>
              <v-input class="short-description-input" v-model="ug.object.lead"/>
              <v-textarea class="short-description-textarea" rows="2" resize="none"
                          v-model="ug.object.lead"/>
              <h3 class="h3">{{ $t('DESCRIPTION') }}</h3>
            </template>
            <div ref="desc__inner">
              <editable-markdown class="heading__text" v-model="ug.object.description"
                                 field="description"/>
            </div>
            <a href="#" class="more" @click.prevent="isDescExpanded = true">{{
                $t('VIEW MORE')
              }}</a>
          </div>
        </div>
        <div class="section section_pt" v-if="areScreenshotsVisible || editMode">
          <h2 class="h2">
            {{ $t('SCREENSHOTS') }}
            <ul class="select">
              <li
                v-for="os in screenshotsOSs"
                class="select__tab"
                :class="{ select__tab_active: screenshotsOS === os}"
                @click="screenshotsOS = os"
                :key="os"
              >
                {{ beautifyDevice(os) }}
              </li>
            </ul>
          </h2>

          <v-button
            v-if="editMode"
            type="primary"
            :loading="uploadingScreenshots"
            @click.native="openScreenshotsFileDialog"
          >
            {{ $t('ADD_SCREENSHOTS') }}
          </v-button>

          <v-draggable
            v-for="(array, os) in ug.object.screenshots_uris_by_os_type"
            v-model="ug.object.screenshots_uris_by_os_type[os]"
            :disabled="!editMode"
            :key="os"
            v-show="screenshotsOS === os && array.length"
          >
            <transition-group tag="ul" class="screenshots">
              <li
                class="screenshots__item"
                :class="{
                  screenshots__item_draggable: editMode
                }"
                v-for="(screenshot, idx) in array"
                :key="screenshot"
              >
                <img class="screenshots__img" :src="screenshot"/>

                <button
                  v-if="editMode"
                  class="screenshots__remove-button"
                  @click="removeScreenshot(os, idx)"
                >
                  <img src="@/assets/icon-x.svg"/>
                </button>
              </li>
            </transition-group>
          </v-draggable>
        </div>
      </div>
      <div class="main__content main__content_b main__content_ptop">
        <div v-if="ug.object.whatsnew || editMode" class="section">
          <h2 class="h2">{{ $t('WHATSNEW') }}</h2>
          <div
            v-if="ug.object.whatsnew || editMode"
            ref="changelog"
            class="changelog"
            :class="{ changelog_expanded: isChangelogExpanded || editMode }"
          >
            <editable-markdown ref="changelog__inner" v-model="ug.object.whatsnew"
                               field="whatsnew"/>
            <a
              href="#"
              class="more"
              @click.prevent="isChangelogExpanded = true"
            >{{ $t('VIEW MORE') }}</a
            >
          </div>
        </div>
        <div class="section section_pt section_noborder">
          <h2 class="h2 h2_badge">
            {{ $t('INFORMATION') }}
          </h2>
          <div class="info">
            <table class="info__list">
              <v-detail v-if="editMode" :label="$t('PUBLIC')" v-model="ug.object.is_public"
                        field="is_public" editable type="binary"/>
              <v-detail v-if="editMode" :label="$t('USE_SIDELOADING_PATCH')"
                        v-model="ug.object.use_sideloading_patch" field="use_sideloading_patch"
                        editable type="binary"/>
              <v-detail v-if="editMode" :label="$t('USE_EXPLICIT_BUNDLE_ID')"
                        v-model="ug.object.use_explicit_bundle_id" field="use_explicit_bundle_id"
                        editable type="binary"/>
              <v-detail v-if="editMode" :label="$t('USE_GROUP_PATCH')"
                        v-model="ug.object.use_group_patch" field="use_group_patch" editable
                        type="binary"/>

              <v-detail v-if="editMode" label="18+" v-model="ug.object.is_18plus" field="is_18plus"
                        editable type="binary"/>

              <v-detail :label="$t('DEVELOPER')" v-model="ug.object.developer_name"
                        field="developer_name" editable filterable/>
              <v-detail :label="$t('WEBSITE')" v-model="ug.object.website_uri" field="website_uri"
                        editable>
                <a class="info__link" :href="ug.object.website_uri" target="_blank">
                  {{ $t('VISIT') }}
                </a>
              </v-detail>

              <v-detail :label="$t('PRICE')" :value="ug.object.price_cents">
                {{ (ug.object.price_cents / 100).toFixed(2) }}
              </v-detail>

              <v-detail :label="$t('CATEGORY')" v-model="ug.object.genre_name" field="genre_name"
                        editable type="select" :options="genresOptions"
                        :filter="{genre_id: ug.object.genre_id}"/>
              <v-detail-warning v-if="editMode">
                  <html-with-links tag="p" :html="$t('APP_CATEGORY_EDIT_WARNING')" />
              </v-detail-warning>
              <v-detail :label="$t('UPDATED')" :value="ug.object.updated_at">
                {{ new Date(ug.object.updated_at).toLocaleDateString() }}
              </v-detail>
              <v-detail :label="$t('VERSION')" :value="ug.object.version"/>
              <v-detail :label="$t('SIZE')" :value="ug.object.size_hr"/>

              <v-detail :label="$t('OS_VERSION', {os: 'iOS'})" v-model="ug.object.min_ios_version"
                        field="min_ios_version" editable>
                {{ $t('REQUIRES_MIN_OS_VERSION', { os: `iOS ${ug.object.min_ios_version}` }) }}
              </v-detail>
              <v-detail :label="$t('OS_VERSION', {os: 'iPadOS'})"
                        v-model="ug.object.min_ipados_version" field="min_ipados_version" editable>
                {{
                  $t('REQUIRES_MIN_OS_VERSION', { os: `iPadOS ${ug.object.min_ipados_version}` })
                }}
              </v-detail>
              <v-detail :label="$t('OS_VERSION', {os: 'tvOS'})" v-model="ug.object.min_tvos_version"
                        field="min_tvos_version" editable>
                {{ $t('REQUIRES_MIN_OS_VERSION', { os: `tvOS ${ug.object.min_tvos_version}` }) }}
              </v-detail>
              <v-detail :label="$t('OS_VERSION', {os: 'macOS'})"
                        v-model="ug.object.min_macos_version" field="min_macos_version" editable>
                {{ $t('REQUIRES_MIN_OS_VERSION', { os: `macOS ${ug.object.min_macos_version}` }) }}
              </v-detail>
              <v-detail :label="$t('OS_VERSION', {os: 'watchOS'})"
                        v-model="ug.object.min_watchos_version" field="min_watchos_version"
                        editable>
                {{
                  $t('REQUIRES_MIN_OS_VERSION', { os: `watchOS ${ug.object.min_watchos_version}` })
                }}
              </v-detail>
              <v-detail :label="$t('OS_VERSION', {os: 'visionOS'})"
                        v-model="ug.object.min_visionos_version" field="min_visionos_version"
                        editable>
                {{
                  $t('REQUIRES_MIN_OS_VERSION', { os: `visionOS ${ug.object.min_visionos_version}` })
                }}
              </v-detail>

              <v-detail :label="$t('LICENSE')" v-model="ug.object.license" field="license"
                        editable>
                <a class="info__link" :href="ug.object.license" target="_blank">
                  {{ $t('VIEW') }}
                </a>
              </v-detail>

              <v-detail :label="$t('TOS')" v-model="ug.object.tos_uri" field="tos_uri" editable>
                <a class="info__link" :href="ug.object.tos_uri" target="_blank">
                  {{ $t('VIEW') }}
                </a>
              </v-detail>
              <v-detail :label="$t('PRIVACY_POLICY')" v-model="ug.object.privacy_policy_uri"
                        field="privacy_policy_uri" editable>
                <a class="info__link" :href="ug.object.privacy_policy_uri" target="_blank">
                  {{ $t('VIEW') }}
                </a>
              </v-detail>

              <v-detail :label="$t('SOURCE')" v-model="ug.object.source_name" field="source_name"
                        editable filterable/>
            </table>
          </div>
          <template v-if="isDeviceLinked">
            <div
              v-if="editMode || isPreview"
              class="actions"
              v-sticky
              sticky-side="bottom"
            >
              <div class="actions__buttons">
                <v-button
                  type="primary"
                  size="lg"
                  :loading="savingChanges"
                  @click.native="saveChanges"
                >
                  {{ $t('SAVE_CHANGES') }}
                </v-button>

                <v-button
                  type="primary"
                  size="lg"
                  :loading="fillingFromAppStore"
                  @click.native="fillFromAppStore"
                >
                  {{ $t('FILL_FROM_APPLE_APP_STORE') }}
                </v-button>

                <v-button
                  type="primary"
                  size="lg"
                  :loading="fillingFromUniversalSearch"
                  @click.native="$modal.show('fillDetailsFromUniversalSearch')"
                >
                  {{ $t('FILL_FROM_ANOTHER_APP') }}
                </v-button>
                <universal-search-modal @app-chosen="fillFromUniversalSearch"/>

                <v-button
                  type="negative"
                  size="lg"
                  @click.native="resetChanges"
                >
                  {{ $t('RESET_CHANGES') }}
                </v-button>

                <v-button
                  v-if="isPreview"
                  size="lg"
                  @click.native="endPreview"
                >
                  {{ $t('END_PREVIEW') }}
                </v-button>
                <v-button
                  v-else
                  size="lg"
                  @click.native="startPreview"
                >
                  {{ $t('PREVIEW') }}
                </v-button>
              </div>
            </div>
            <div
              v-else
              class="actions actions_center"
              v-sticky
              sticky-side="bottom"
            >
              <div class="actions__buttons">
                <v-button
                  size="lg"
                  @click.native="share"
                >
                  {{ $t('SHARE') }}
                </v-button>

                <v-button
                  v-if="ug.object.can_be_imported_to_library"
                  size="lg"
                  :loading="isImporting"
                  @click.native="importToLibrary"
                >
                  {{ $t('IMPORT') }}
                </v-button>

                <v-button
                  v-if="!ug.no_download_ticket_failure_reason"
                  tag="router-link"
                  :to="downloadLink"
                  target="_blank"
                  size="lg"
                >
                  {{ $t('DOWNLOAD') }}
                </v-button>

                <v-button
                  v-if="!ug.no_installation_ticket_failure_reason"
                  type="primary"
                  size="lg"
                  :loading="isInstalling"
                  @click.native="install"
                >
                  {{ $t('INSTALL') }}
                </v-button>

                <sharing-modal/>
              </div>
              <div v-if="ug.object.detected_malware" class="actions__notice actions__notice_danger">
                <p>{{ $t('MALWARE_DETECTED_NOTICE') }}</p>
                <p v-for="malware of ug.object.detected_malware" :key="malware.name">
                  {{ malware.name }}, {{ malware.type }}. {{ $t('LEVEL') }}:&nbsp;{{ malware.level }}.
                  {{ $t('DANGER') }}:&nbsp;{{ malware.danger }}
                </p>
              </div>
              <div v-else-if="ug.object.type === 'repo_app'" class="actions__notice">
                <p>{{ $t('REPO_APP_MALWARE_NOTICE') }}</p>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import Loader from '@/components/Loader';
import detailsViewMixin from '@/mixins/detailsView.js';
import installApp from '@/mixins/installApp';
import VButton from '@/components/Button.vue';
import { searchITunesUrl } from '@/utils/searchITunes';
import { cloneDeep } from 'lodash';
import VDraggable from 'vuedraggable';
import Sticky from 'vue-sticky-directive';
import { mapGetters, mapState } from 'vuex';
import VTextarea from '@/components/VTextarea.vue';
import VInput from '@/components/VInput.vue';
import BackLink from '@/components/BackLink.vue';
import { operatingSystems } from '@/data';
import SharingModal from '@/components/modals/SharePageModal.vue';
import HtmlWithLinks from '@/components/HtmlWithLinks.vue';
import EditableMarkdown from './components/EditableMarkdown.vue';
import EditableLine from './components/EditableLine.vue';
import VDetail from './components/VDetail.vue';
import VDetailWarning from './components/VDetailWarning.vue';
import UniversalSearchModal from './components/UniversalSearchModal.vue';

export default {
  mixins: [detailsViewMixin, installApp],
  directives: { Sticky },
  components: {
    Loader,
    EditableMarkdown,
    EditableLine,
    VDetail,
    VDetailWarning,
    VButton,
    UniversalSearchModal,
    VDraggable,
    VTextarea,
    VInput,
    BackLink,
    SharingModal,
    HtmlWithLinks,
  },
  data() {
    return {
      isDescExpanded: false,
      screenshotsOS: '',
      isChangelogExpanded: false,
      isLoaded: false,
      isInstalling: false,
      prevRoute: null,
      linkToInstallError: false,
      ug: {
        object: {
          screenshots_uris_by_os_type: {},
        },
      },
      ugCopy: {},
      iconFailedToLoad: false,
      savingChanges: false,
      screenshotsInput: null,
      uploadingScreenshots: false,
      fillingFromAppStore: false,
      fillingFromUniversalSearch: false,
      primitiveEditableKeys: ['name', 'is_18plus', 'lead', 'description', 'whatsnew', 'source_name', 'developer_name', 'genre_name', 'min_ios_version', 'min_ipados_version', 'min_tvos_version', 'min_macos_version', 'min_watchos_version', 'min_visionos_version', 'website_uri', 'license', 'tos_uri', 'privacy_policy_uri'],
      isImporting: false,
    };
  },
  computed: {
    ...mapState(['genres']),
    ...mapGetters(['isDeviceLinked']),
    title() {
      return this.ug.object.name;
    },
    universalObjectId() {
      return this.$route.params.id;
    },
    id() {
      return this.$route.query.id;
    },
    hasIcon() {
      return this.ug.object.icon_uri && !this.iconFailedToLoad;
    },
    areScreenshotsVisible() {
      if (!this.ug.object.screenshots_uris_by_os_type) {
        return false;
      }

      const allScreenshots = Object.values(this.ug.object.screenshots_uris_by_os_type)
        .flat();

      return Boolean(allScreenshots.length);
    },
    screenshotsOSs() {
      if (this.editMode) {
        return ['universal', 'ios', 'ipados', 'macos', 'tvos', 'visionos'];
      }

      if (!this.ug.object.screenshots_uris_by_os_type) {
        return [];
      }

      return Object.entries(this.ug.object.screenshots_uris_by_os_type)
        .filter(([, uris]) => uris?.length)
        .map(([os]) => os);
    },
    devicesDictionary() {
      const entries = operatingSystems.map(({
        name,
        device,
      }) => [name, device]);
      entries.push(['universal', this.$t('UNIVERSAL_SCREENSHOTS_TITLE')]);

      return Object.fromEntries(entries);
    },
    genresOptions() {
      return Object.values(this.genres.official)
        .map(({ name }) => ({
          label: name,
          value: name,
        }));
    },
    backLink() {
      return {
        isProgrammatic: Boolean(this.prevRoute?.matched.length),
        to: this.prevRoute?.fullPath || '/',
        label: this.$t('GO_BACK'),
      };
    },
    downloadLink() {
      if (!this.ug.download_ticket) {
        return '';
      }

      return `/redirect/${this.ug.download_ticket}`;
    },
    installationFailureReason() {
      return this.ug.no_installation_ticket_failure_reason?.translated;
    },
    downloadFailureReason() {
      return this.ug.no_download_ticket_failure_reason?.translated;
    },
  },
  methods: {
    beautifyDevice(device) {
      return this.devicesDictionary[device] || device;
    },
    async handleTriggers() {
      await this.$nextTick();

      const descWrapper = this.$refs.desc && this.$refs.desc.getBoundingClientRect().bottom;
      const descText = this.$refs.desc__inner
        && this.$refs.desc__inner.getBoundingClientRect().bottom;

      if (descText <= descWrapper) {
        this.isDescExpanded = true;
      }

      if (this.$refs.changelog__inner
        && this.$refs.changelog__inner.$el.getBoundingClientRect().height <= 85) {
        this.isChangelogExpanded = true;
      }
    },
    async getInfo() {
      this.isLoaded = false;

      const response = await this.$api.universalGateway({
        universalObjectId: this.universalObjectId,
        id: this.id,
        silent: true,
      });

      if (!response.success) {
        const message = response.errors.map(error => error.translated)
          .join('\n');
        this.$router.replace({
          name: 'error',
          params: {
            message,
            link: '/',
            linkText: 'GO_TO_MAIN_PAGE',
          },
        });
        return;
      }

      this.ug = response.data;
      this.ugCopy = cloneDeep(response.data);

      this.isLoaded = true;
    },
    async fillFromAppStore() {
      const url = await this.$prompt('Apple App Store URL');

      this.fillingFromAppStore = true;

      const app = await searchITunesUrl(url);
      this.fillingFromAppStore = false;

      if (!app) {
        this.$notify({
          type: 'error',
          text: this.$t('APP_NOT_FOUND'),
        });
        return;
      }

      this.ug.object.description = app.description;
      this.ug.object.whatsnew = app.releaseNotes;
      this.ug.object.developer_name = app.artistName;
      this.ug.object.genre_name = app.primaryGenreName;
    },
    async fillFromUniversalSearch(preview) {
      this.$modal.hide('fillDetailsFromUniversalSearch');
      this.fillingFromUniversalSearch = true;

      try {
        const app = await this.$api.universalGateway({
          universalObjectId: preview.universal_object_identifier,
          id: preview.id,
        });

        const keysToCopy = [...this.primitiveEditableKeys, 'screenshots_uris_by_os_type'];

        for (const key of keysToCopy) {
          this.ug.object[key] = app.object[key];
        }
      } finally {
        this.fillingFromUniversalSearch = false;
      }
    },
    async openScreenshotsFileDialog() {
      if (!this.screenshotsInput) {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = 'image/png';
        input.hidden = true;

        input.addEventListener('change', async () => {
          this.uploadingScreenshots = true;
          try {
            const os = this.screenshotsOS;
            const uploads = Array.from(input.files)
              .map(file => this.$api.uploadFile({
                type: 'screenshot',
                file,
              }));
            const urls = await Promise.all(uploads);

            if (!this.ug.object.screenshots_uris_by_os_type) {
              this.$set(this.ug.object, 'screenshots_uris_by_os_type', {});
            }

            if (!this.ug.object.screenshots_uris_by_os_type[os]) {
              this.$set(this.ug.object.screenshots_uris_by_os_type, os, []);
            }

            this.ug.object.screenshots_uris_by_os_type[os].push(...urls);
          } finally {
            this.uploadingScreenshots = false;
          }

          input.value = null;
        });

        this.screenshotsInput = input;
        document.body.appendChild(input);
      }

      this.screenshotsInput.click();
    },
    removeScreenshot(os, idx) {
      this.ug.object.screenshots_uris_by_os_type[os].splice(idx, 1);
    },
    async saveChanges() {
      this.savingChanges = true;
      this.setFieldWithError('');

      const keysToSave = ['id', 'is_public', 'use_sideloading_patch', 'use_explicit_bundle_id', 'use_group_patch', ...this.primitiveEditableKeys];
      const metadata = Object.fromEntries(Object.entries(this.ug.object)
        .filter(([key]) => keysToSave.includes(key)));
      if (this.ug.object.screenshots_uris_by_os_type) {
        metadata.screenshots_uris_by_os_type = JSON.stringify(this.ug.object.screenshots_uris_by_os_type);
      }

      try {
        const response = await this.$api.editIpaMetadata(metadata);
        if (response.success) {
          this.$notify({
            type: 'success',
            text: this.$t('DETAILS_CHANGES_SAVED_NOTIFICATION'),
          });
          this.startPreview();
        } else {
          this.$notify({
            type: 'error',
            text: response.errors.map(error => error.translated)
              .join('\n'),
          });

          if (response.errors.some(error => error.code === 'ERROR_INCOMPLETE_METADATA')) {
            this.setFieldWithError(response.data);
          }
        }
      } finally {
        this.savingChanges = false;
      }
    },
    async resetChanges() {
      await this.$confirm(this.$t('RESET_DETAILS_CHANGES_CONFIRMATION'));
      this.ug = cloneDeep(this.ugCopy);
    },
    handle18PlusContent() {
      if (this.ug.object.is_18plus !== 1 || this.editMode) {
        return;
      }

      this.$confirm(this.$t('AGE_RESTRICTED_CONTENT_ALERT'), {
        hidePage: true,
        closeManually: true,
        okText: 'GO BACK',
        cancelText: 'IM_18_PLUS',
      })
        .then(() => {
          this.$router.push(this.prevRoute?.fullPath || '/');
        })
        .finally(() => {
          this.$modal.hide('dialog');
        });
    },
    async install() {
      this.isInstalling = true;
      try {
        await this.installApp(this.ug.object.id, 'universal', { installationTicket: this.ug.installation_ticket });
      } finally {
        this.isInstalling = false;
      }
    },
    async importToLibrary() {
      await this.$confirm(this.$t('APP_IMPORT_TO_LIBRARY_CONFIRMATION'), { okText: this.$t('IMPORT') });
      this.isImporting = true;
      try {
        await this.$api.importToLibrary(this.ug.object.universal_object_identifier);
        this.$notify({
          type: 'success',
          text: this.$t('APP_IMPORT_TO_LIBRARY_SUCCESS'),
        });
      } finally {
        this.isImporting = false;
      }
    },
    async share() {
      this.$modal.show('sharePage');
    },
    async fixStickyDirective() {
      await this.$nextTick();
      window.dispatchEvent(new Event('resize'));
    },
  },
  watch: {
    isLoaded(newVal) {
      if (newVal) {
        this.fixStickyDirective();
        this.handleTriggers();
        [this.screenshotsOS] = this.screenshotsOSs;
      }
    },
    isPreview() {
      this.handleTriggers();
      this.fixStickyDirective();
    },
    ug: {
      handler() {
        this.handle18PlusContent();
      },
      deep: true,
    },
    '$route.params.id': function () {
      this.getInfo();
    },
  },
  created() {
    this.getInfo();
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRoute = from;
    });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

@mixin gradient {
  &:before {
    content: "";
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 50px;
    background-image: var(--app-fadeout-gradient);
    pointer-events: none;
  }

  &_expanded {
    max-height: unset;

    &:before,
    .more {
      display: none;
    }
  }
}

.main {
  display: grid;
  grid-template-areas:
    "loader"
    "content-a"
    "content-b";
  grid-template-columns: minmax(0, 1fr);

  @include notebook {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "loader"
      "content-a"
      "sidebar"
      "content-b";
  }

  &__loader {
    grid-area: loader;
  }

  &__content {
    &_a {
      grid-area: content-a;
    }

    &_b {
      grid-area: content-b;
    }

    &_ptop {
      padding-top: 30px;

      @include notebook {
        padding-top: 10px;
      }
    }

    @include notebook {
      max-width: 100%;
    }
  }

  &__sidebar {
    grid-area: sidebar;

    @include notebook {
      width: 100%;
    }
  }
}

.section {
  padding-bottom: 30px;
  border-bottom: 1px solid #e5e5e5;

  @include notebook {
    padding-bottom: 10px;
  }

  &_noborder {
    border: none;
  }

  &_pt {
    padding-top: 30px;

    @include notebook {
      padding-top: 10px;
    }
  }
}

.heading {
  display: flex;

  &__desc {
    max-height: 227px;
    overflow: hidden;
    position: relative;
    flex-grow: 1;

    &_book {
      max-height: 340px;
    }

    @include gradient;
  }

  &__mobile {
    margin-bottom: 15px;

    @include notebook {
      display: flex;
      align-items: center;
    }

    @include notebook {
      margin-bottom: 15px;
    }
  }

  &__icon,
  &__icon-stub {
    flex: 0 0 227px;
    height: 227px;
    margin-right: 27px;
    border-radius: 40px;

    &:not(&_vertical) {
      border-radius: 22.5%;
      mask: url(../../assets/ios-mask.svg) center/100% 100% no-repeat;
    }

    @include notebook {
      display: none;
    }

    &_vertical {
      height: 340px;
    }

    &,
    &-mobile {
      object-fit: cover;
      object-position: center;
    }

    &-mobile {
      display: none;

      @include notebook {
        display: block;
        width: 64px;
        flex: 0 0 64px;
        height: 64px;
        border-radius: 12px;
        margin-right: 10px;
      }

      &_vertical {
        height: 96px;
      }
    }
  }

  &__icon-stub {
    &,
    &-mobile {
      background-color: var(--stub-bg);
    }
  }

  &__title {
    display: flex;
    align-items: center;
    color: var(--text-tertiary);
    font-size: 28px;
    font-weight: 600;
    min-width: 0;

    @include notebook {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      margin-bottom: 0;
    }

    &-inner {
      @include notebook {
        width: 100%;
      }
    }
  }

  &__badge {
    padding: 5px;
    color: #2b2b05;
    text-transform: uppercase;
    font-size: 14px;
    border-radius: 2px;
    background-color: var(--bg-warning);
    margin-left: 11px;

    @include notebook {
      margin-left: 0;
      margin-top: 5px;
    }
  }

  &__text {
    line-height: 16px;
  }

  &__separator {
    content: "";
    display: block;
    margin-top: 10px;
  }
}

.more {
  display: flex;
  align-items: center;
  color: $primary;
  font-weight: 500;
  position: absolute;
  left: 0;
  bottom: 0;

  @include notebook {
    right: 0;
    justify-content: center;
  }

  &:after {
    content: "";
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 4px 4px 0 4px;
    border-color: $primary transparent transparent transparent;
    margin-left: 3px;
    margin-top: 2px;
  }
}

.h2 {
  color: var(--text-tertiary);
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  @include notebook {
    margin-bottom: 10px;
  }

  &_badge {
    @include notebook {
      flex-direction: column;
      align-items: flex-start;
    }
  }
}

.h3 {
  color: var(--text-tertiary);
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.select {
  display: flex;
  flex-wrap: wrap;
  margin-left: 20px;
  border-radius: 2px;

  @include notebook {
    margin-left: 10px;
  }

  &__tab {
    position: relative;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 400;
    flex-grow: 1;
    padding: 3px 25px;
    border: 1px solid rgba(96, 112, 128, 0.4);
    cursor: pointer;
    text-align: center;

    &:first-child {
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
    }

    &:last-child {
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
    }

    &:before,
    &:after {
      content: "";
      display: none;
      position: absolute;
      top: -1px;
      bottom: -1px;
      width: 1px;
      background-color: rgba(96, 112, 128, 0.4);
    }

    &_active {
      background-color: var(--lighter-primary);
      border-color: lighten($primary, 30%);
      z-index: 1;

      &:before,
      &:after {
        background-color: lighten($primary, 30%);
      }
    }

    &:not(:first-child) {
      border-left: none;

      &:before {
        display: block;
        left: -1px;
      }
    }

    &:not(:last-child) {
      border-right: none;

      &:after {
        display: block;
        right: 0;
      }
    }
  }
}

.screenshots {
  display: flex;
  overflow: scroll;
  padding: 15px 0;

  &__item {
    position: relative;

    &_draggable {
      cursor: grab;
    }

    &:not(:last-child) {
      margin-right: 5px;
    }
  }

  &__img {
    height: 350px;
  }

  &__remove-button {
    position: absolute;
    right: -2px;
    top: -5px;
    background: var(--text-error);
    width: 20px;
    height: 20px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 15px;
    }
  }
}

.changelog {
  display: inline-block;
  line-height: 16px;
  max-height: 85px;
  overflow: hidden;
  position: relative;
  width: 100%;
  @include gradient;
}

.ul {
  list-style: disc inside;
  margin-top: 5px;
}

.info-badge {
  background: var(--lighter-primary);
  border: 1px solid lighten($primary, 45%);
  border-radius: 2px;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 400;
  color: var(--text-primary);
  margin-left: 10px;

  @include notebook {
    margin-left: 0;
    margin-top: 5px;
  }
}

.info {
  display: flex;
  margin-bottom: 20px;

  @include notebook {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 10px;
  }

  &__item {
    margin-bottom: 5px;
    line-height: 20px;
  }

  &__link {
    color: $primary;
    font-weight: 500;
  }

  &__intro {
    color: #525d66;
    margin-right: 15px;
  }

  &__downloads {
    margin-left: auto;

    @include notebook {
      margin-left: 0;
      margin-top: 10px;
    }

    &-heading {
      color: var(--text-tertiary);
      font-weight: 600;
      font-size: 18px;
      margin-bottom: 5px;
    }

    &-total {
      font-weight: 500;
      line-height: 20px;
    }
  }

  &__download {
    margin-bottom: 5px;
    line-height: 20px;
  }
}

.back-link {
  + .section {
    margin-top: 30px;

    @include notebook {
      margin-top: 10px;
    }
  }
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 20px 0;
  background: var(--bg);
  border-top: 1px solid #e5e5e5;

  @include notebook {
    padding: 15px 0;
  }

  &__buttons {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  &__notice {
    word-break: break-word;
    margin: 0 auto;
    text-align: center;
    flex-shrink: 1;
    color: var(--text-warning);

    &_danger {
      color: var(--text-error);
    }

    p:not(:first-child) {
      margin-top: 5px;
    }
  }
}

.short-description {
  font-size: 20px;
  margin-top: 5px;

  @include notebook {
    font-size: 16px;
    margin-top: 3px;
    padding-bottom: 3px;
  }
}

.short-description-input,
.short-description-textarea {
  margin-bottom: 15px;
}

.short-description-textarea {
  display: none;

  @include tablet {
    display: block;
  }
}

.short-description-input {
  display: block;
  width: 100%;

  @include tablet {
    display: none;
  }
}

.banners {
  margin: 15px 0;
}

.banner {
  background: var(--lighter-primary);
  padding: 10px;
  text-align: center;
  border-radius: 5px;

  + .banner {
    margin-top: 10px;
  }
}
</style>
